import { action, makeObservable, observable, observe, runInAction } from 'mobx';
import AuthStore from './AuthStore';
import Reseller from '../model/reseller';
import Company from '../model/Company';

import { createResellerCompany, getResellerCompanies } from '../api/admin';
import CreateCompanyDto from '../model/CreateCompanyDto';

class ResellerStore {
  reseller?: Reseller | null = null;

  companies: Company[] = [];

  loading = false;

  endReached = false;

  rowsPerPage = 10;

  createCompanyError = '';

  constructor(authStore: AuthStore) {
    makeObservable(this, {
      reseller: observable,
      companies: observable,
      loading: observable,
      getCompanies: action,
      refreshCompanies: action,
      createCompanyError: observable,
    });

    observe(authStore, 'reseller', () => {
      if (authStore.user && authStore.isReseller) {
        runInAction(() => {
          this.reseller = authStore.reseller;
        });
      }
    });

    observe(this, 'reseller', () => {
      this.getCompanies();
    });
  }

  getCompanies = async (): Promise<void> => {
    if (this.reseller && this.reseller.id) {
      this.endReached = false;
      this.loading = true;
      await getResellerCompanies(this.reseller.id)
        .then((res) => {
          if (res.length < this.rowsPerPage) {
            this.endReached = true;
          }
          runInAction(() => {
            this.companies = res;
          });
        })
        .finally(() => {
          runInAction(() => {
            this.loading = false;
          });
        });
    }
  };

  getMoreCompanies = async (): Promise<void> => {
    if (this.reseller && this.companies.length > 0 && !this.loading && !this.endReached) {
      this.loading = true;
      const companies = await getResellerCompanies(
        this.reseller.id,
        this.companies[this.companies.length - 1].id,
      );
      runInAction(() => {
        if (companies.length < this.rowsPerPage) {
          this.endReached = true;
        }
        this.companies = [...this.companies, ...companies];
        this.loading = false;
      });
    }
  };

  refreshCompanies = async (): Promise<void> => {
    if (this.loading) {
      return;
    }
    this.companies = [];
    this.getCompanies();
  };

  createCompany = async (company: CreateCompanyDto): Promise<Company | void> => {
    if (this.reseller && this.reseller.id && company) {
      return createResellerCompany(this.reseller.id, company)
        .then((res) => {
          runInAction(() => {
            this.companies = [...this.companies, res];
            this.createCompanyError = '';
          });
          return res;
        })
        .catch((err) => {
          runInAction(() => {
            this.createCompanyError = err.name;
          });
        });
    }
    throw new Error('Reseller not found');
  };

  setCreateCompanyError = (error: string): void => {
    this.createCompanyError = error;
  };
}

export default ResellerStore;
